import { useEffect, useState } from 'react'
import { aOrganization, useAtomValue } from '~/atoms'
import type { LocationBounds, LocationData } from './useCentroidLocation'
import { isGoogleMapsAvailable } from './useCentroidLocation'

function findAddressComponent(
  result: google.maps.GeocoderResult,
  types: string[]
): google.maps.GeocoderAddressComponent | undefined {
  return result.address_components.find((comp) =>
    types.some((type) => comp.types.includes(type))
  )
}

function extractCityFromResult(result: google.maps.GeocoderResult): string {
  const city = findAddressComponent(result, [
    'locality',
    'administrative_area_level_3',
  ])

  return city?.long_name || ''
}

function extractStateFromResult(result: google.maps.GeocoderResult): string {
  const state = findAddressComponent(result, ['administrative_area_level_1'])

  return state?.short_name || ''
}

function extractPostalCodeFromResult(
  result: google.maps.GeocoderResult
): string {
  const postal = findAddressComponent(result, ['postal_code'])

  return postal?.long_name || ''
}

function extractCountyFromResult(result: google.maps.GeocoderResult): string {
  const county = findAddressComponent(result, ['administrative_area_level_2'])

  return county?.long_name?.replace('County', '').trim() || ''
}

async function getPlaceDetailsByPlaceId(
  placeId: string
): Promise<google.maps.GeocoderResult | null> {
  return new Promise((resolve, reject) => {
    if (!window.google || !window.google.maps) {
      resolve(null)
      return
    }

    const geocoder = new google.maps.Geocoder()

    geocoder.geocode({ placeId }, (results, status) => {
      if (status === window.google.maps.GeocoderStatus.OK && results?.[0]) {
        resolve(results[0])
      } else {
        reject(null)
      }
    })
  })
}

function extractCoordinatesFromResult(result: google.maps.GeocoderResult): {
  latitude: number
  longitude: number
} {
  return {
    latitude: result.geometry.location.lat(),
    longitude: result.geometry.location.lng(),
  }
}

function extractLocationDataFromResult(
  result: google.maps.GeocoderResult
): LocationData {
  const coordinates = extractCoordinatesFromResult(result)

  return {
    city: extractCityFromResult(result),
    stateAbbr: extractStateFromResult(result),
    postalCode: extractPostalCodeFromResult(result),
    county: extractCountyFromResult(result),
    latitude: coordinates.latitude,
    longitude: coordinates.longitude,
  }
}

function createLocationBounds(
  bounds?: LocationBounds
): google.maps.LatLngBounds | undefined {
  if (!bounds || !isGoogleMapsAvailable()) {
    return undefined
  }

  return new google.maps.LatLngBounds(
    new google.maps.LatLng(bounds[0][0], bounds[0][1]),
    new google.maps.LatLng(bounds[1][0], bounds[1][1])
  )
}

async function makeAutocompleteRequest(
  input: string,
  types: (
    | 'locality'
    | 'administrative_area_level_2'
    | 'postal_code'
    | 'geocode'
  )[],
  locationRestriction?: google.maps.LatLngBounds
): Promise<google.maps.places.AutocompletePrediction[] | null> {
  return new Promise((resolve, reject) => {
    if (!window.google || !window.google.maps) {
      resolve(null)

      return
    }

    const service = new google.maps.places.AutocompleteService()

    service.getPlacePredictions(
      {
        input,
        types: types.length === 1 ? [types[0]] : ['(cities)'],
        componentRestrictions: { country: 'us' },
        locationRestriction,
      },
      (predictions, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          const filteredPredictions =
            predictions?.filter((prediction) => {
              if (types.includes('postal_code')) {
                return prediction.types.includes('postal_code')
              }
              if (types.includes('locality') || types.includes('geocode')) {
                return prediction.types.some((type) =>
                  [
                    'locality',
                    'sublocality',
                    'administrative_area_level_3',
                  ].includes(type)
                )
              }

              if (types.includes('administrative_area_level_2')) {
                return prediction.types.includes('administrative_area_level_2')
              }

              return false
            }) || []

          resolve(filteredPredictions)
        } else {
          reject(null)
        }
      }
    )
  })
}

function getPredictionMergeKey(
  prediction: google.maps.places.AutocompletePrediction
) {
  return prediction.place_id || prediction.description
}

export function usePlaceSearch(searchType: 'city' | 'county' | 'postalCode') {
  const organization = useAtomValue(aOrganization)
  const [search, setSearch] = useState<string>('')
  const [isLoading, setIsLoading] = useState(false)
  const [predictions, setPredictions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([])

  let debounceTimeout: ReturnType<typeof setTimeout> | null = null

  function debounceSearch(input: string) {
    if (input.length === 0) {
      setPredictions([])
      setIsLoading(false)
    } else if (input.length >= 2) {
      if (debounceTimeout !== null) {
        clearTimeout(debounceTimeout)
      }

      setIsLoading(true)

      debounceTimeout = setTimeout(async () => {
        try {
          const bounds = createLocationBounds(
            organization?.options.placeSearchBounds as
              | LocationBounds
              | undefined
          )

          const types =
            searchType === 'city'
              ? ['locality' as const]
              : searchType === 'county'
                ? ['administrative_area_level_2' as const]
                : ['postal_code' as const]

          const predictions = await makeAutocompleteRequest(
            input,
            types,
            bounds
          )

          if (searchType === 'city') {
            const geocodePredictions = await makeAutocompleteRequest(
              input,
              ['geocode' as const],
              bounds
            )

            const mergedPredictions = [
              ...(predictions || []),
              ...(geocodePredictions || []),
            ]

            const seen = new Set<string>()
            setPredictions(
              mergedPredictions.filter((prediction) => {
                const key = getPredictionMergeKey(prediction)
                if (!key || seen.has(key)) {
                  return false
                }

                seen.add(key)
                return true
              })
            )
            setIsLoading(false)
            return
          }

          setPredictions(predictions || [])
          setIsLoading(false)
        } catch (_) {
          setPredictions([])
          setIsLoading(false)
        }
      }, 200)
    }
  }

  async function getLocationData(
    prediction: google.maps.places.AutocompletePrediction
  ) {
    const place = await getPlaceDetailsByPlaceId(prediction.place_id)

    if (!place) {
      return null
    }

    return extractLocationDataFromResult(place)
  }

  useEffect(() => {
    debounceSearch(search)

    return () => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout)
      }
    }
  }, [search])

  return {
    search,
    setSearch,
    isLoading,
    predictions,
    setPredictions,
    getLocationData,
  }
}
